/* eslint-disable no-dupe-keys */
// Configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'https://api.jamboricg.com/api/mbeleko',
  TOKEN_KEY: 'authToken',
  TIMEOUT: 30000, // 30 seconds
};

// Token management utilities
const TokenManager = {
  /**
   * Get token from storage (prioritizing localStorage)
   */
  getToken() {
    return localStorage.getItem(API_CONFIG.TOKEN_KEY) || 
           localStorage.getItem('token') || 
           sessionStorage.getItem('authToken') || 
           sessionStorage.getItem('token');
  },

  /**
   * Set token in storage
   */
  setToken(token) {
    if (token) {
      localStorage.setItem(API_CONFIG.TOKEN_KEY, token);
      // Remove old token keys to avoid confusion
      localStorage.removeItem('token');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('token');
      console.log('✅ Token saved successfully');
    }
  },

  /**
   * Clear all tokens
   */
  clearToken() {
    localStorage.removeItem(API_CONFIG.TOKEN_KEY);
    localStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('token');
    console.log('🧹 All tokens cleared');
  },

  /**
   * Check if we have a valid token
   */
  hasToken() {
    const token = this.getToken();
    return !!(token && token.length > 10); // Basic validation
  }
};

// HTTP client with proper error handling
class APIClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  /**
   * Get headers with proper authentication
   */
  getHeaders(contentType = 'application/json') {
    const headers = {
      'Content-Type': contentType,
      'Accept': 'application/json',
    };

    const token = TokenManager.getToken();
    if (token) {
      // FIXED: Use 'Token' instead of 'Bearer' for DRF TokenAuthentication
      headers['Authorization'] = `Token ${token}`;
    }

    return headers;
  }

  /**
   * Make HTTP request with proper error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      timeout: API_CONFIG.TIMEOUT,
      headers: this.getHeaders(),
      ...options,
    };

    console.log(`🌐 ${config.method || 'GET'} ${url}`);
    
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log(`📡 Response: ${response.status} ${response.statusText}`);

      // Handle different response types
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        console.error(`❌ HTTP Error ${response.status}:`, data);
        
        // Handle authentication errors
        if (response.status === 401) {
          console.warn('🔐 Authentication failed - clearing tokens');
          TokenManager.clearToken();
          
          // Optionally redirect to login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        
        // Throw structured error
        const error = new Error(this.getErrorMessage(data, response.status));
        error.status = response.status;
        error.data = data;
        throw error;
      }

      console.log(`✅ Success:`, data);
      return data;

    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏰ Request timeout');
        throw new Error('Request timeout. Please check your connection.');
      }
      
      console.error(`💥 Request failed:`, error);
      throw error;
    }
  }

  /**
   * Extract meaningful error message
   */
  getErrorMessage(data, status) {
    if (typeof data === 'string') return data;
    
    // Handle Django REST Framework error formats
    if (data?.detail) return data.detail;
    if (data?.error) return data.error;
    if (data?.message) return data.message;
    
    // Handle field errors
    if (data && typeof data === 'object') {
      const firstError = Object.values(data)[0];
      if (Array.isArray(firstError)) {
        return firstError[0];
      }
      if (typeof firstError === 'string') {
        return firstError;
      }
    }
    
    // Default error messages
    const defaultMessages = {
      400: 'Invalid request data',
      401: 'Authentication required',
      403: 'Permission denied',
      404: 'Resource not found',
      500: 'Server error',
    };
    
    return defaultMessages[status] || `HTTP Error ${status}`;
  }

  // HTTP method helpers
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, { method: 'GET' });
  }

  async post(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async patch(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async put(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Create API client instance
const apiClient = new APIClient(API_CONFIG.BASE_URL);

// Main Kileleni Service
export const MbelekoService = {
  // ==================== AUTHENTICATION ====================
  
  /**
   * Initialize service with token
   */
  init(token) {
    if (token) {
      TokenManager.setToken(token);
      console.log('✅ MbelekoService initialized');
    } else {
      console.warn('⚠️ No token provided to init');
    }
  },

  /**
   * Set authentication token
   */
  setToken(token) {
    TokenManager.setToken(token);
  },

  /**
   * Get current token
   */
  getToken() {
    return TokenManager.getToken();
  },

  /**
   * Check if authenticated
   */
  isAuthenticated() {
    return TokenManager.hasToken();
  },

  /**
   * Logout and clear tokens
   */
  logout() {
    TokenManager.clearToken();
  },


   // Generic API call method similar to your apiClient pattern
  async apiCall(endpoint, options = {}) {
    const url = `${this.getApiUrl()}${endpoint}`;
    
    const config = {
      headers: {
        'Authorization': `Token ${this.getToken()}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      ...options,
    };

    console.log(`API Call: ${config.method || 'GET'} ${url}`);
    
    try {
      const response = await fetch(url, config);

      console.log(`Response: ${response.status} ${response.statusText}`);

      // Check if response is HTML (server error)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('API server not responding correctly. Please check if the backend server is running.');
      }
      
      // Handle different response types
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle HTTP errors
      if (!response.ok) {
        console.error(`HTTP Error ${response.status}:`, data);
        
        // Handle authentication errors
        if (response.status === 401) {
          console.warn('Authentication failed - clearing tokens');
          // Clear tokens if needed
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('token');
        }
        
        // Extract error message
        let errorMessage = 'Request failed';
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data?.detail) {
          errorMessage = data.detail;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (data?.message) {
          errorMessage = data.message;
        } else if (data && typeof data === 'object') {
          // Handle field errors
          const firstError = Object.values(data)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      console.log('Success:', data);
      return data;

    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  },
  /**
   * Test API connection
   */
  async testConnection() {
    try {
      console.log('🧪 Testing API connection...');
      
      if (!this.isAuthenticated()) {
        throw new Error('No authentication token available');
      }
      
      // Test with a simple endpoint
      const data = await apiClient.get('/daraja-levels/');
      console.log('✅ API connection successful');
      return { success: true, data };
      
    } catch (error) {
      console.error('❌ API connection failed:', error);
      return { success: false, error: error.message };
    }
  },

  // ==================== USER MANAGEMENT ====================
  
  async searchUsers(query) {
    if (!query || query.length < 3) {
      throw new Error('Search query must be at least 3 characters');
    }
    return apiClient.get('/search-users/', { q: query });
  },

  // ==================== DARAJA LEVELS ====================
  
  async getDarajaLevels() {
    return apiClient.get('/daraja-levels/');
  },

  // ==================== GROUP MANAGEMENT ====================
  
  async getGroups(params = {}) {
    return apiClient.get('/groups/', params);
  },

  async getGroup(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/`);
  },

  async createGroup(groupData) {
    if (!groupData) throw new Error('Group data is required');
    return apiClient.post('/groups/create/', groupData);
  },

  async updateGroup(groupId, updateData) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.patch(`/groups/${groupId}/`, updateData);
  },

  async getGroupMembers(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/members/`);
  },

  async addMemberToGroup(groupId, memberData) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.post(`/groups/${groupId}/add-member/`, memberData);
  },

  async removeMemberFromGroup(groupId, memberId) {
    if (!groupId || !memberId) throw new Error('Group ID and Member ID are required');
    return apiClient.delete(`/groups/${groupId}/members/${memberId}/remove/`);
  },

  // ==================== MEMBER ACCOUNTS ====================
  
  async getMemberAccounts(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/my-accounts/`);
  },

  async getAccountTransactions(accountId, params = {}) {
    if (!accountId) throw new Error('Account ID is required');
    return apiClient.get(`/accounts/${accountId}/transactions/`, params);
  },

  async getMemberDashboard(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/dashboard/`);
  },

  // ==================== COMPULSORY PHASE ====================
  
  async initializeCompulsoryPhase(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.post(`/groups/${groupId}/initialize-compulsory/`);
  },

  // ==================== CYCLE MANAGEMENT ====================
  
  async startCycle(groupId, cycleData = {}) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.post(`/groups/${groupId}/start-cycle/`, cycleData);
  },

  async triggerMarundoPhase(cycleId) {
    if (!cycleId) throw new Error('Cycle ID is required');
    return apiClient.post(`/cycles/${cycleId}/trigger-marundo/`);
  },

  async getGroupPhaseStatus(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/phase-status/`);
  },

  // ==================== CONTRIBUTIONS ====================
  
  async getKileleniContributions(params = {}) {
    const { group_id, ...queryParams } = params;
    const endpoint = group_id 
      ? `/groups/${group_id}/contributions/kileleni/`
      : '/contributions/kileleni/';
    
    return apiClient.get(endpoint, queryParams);
  },

  async payKileleniContribution(contributionId, paymentData) {
    if (!contributionId) throw new Error('Contribution ID is required');
    return apiClient.post(`/contributions/kileleni/${contributionId}/pay/`, paymentData);
  },

  async payViaMobileMoney(contributionId, mobilePaymentData) {
    if (!contributionId) throw new Error('Contribution ID is required');
    return apiClient.post(`/contributions/kileleni/${contributionId}/mobile-pay/`, mobilePaymentData);
  },

  // ==================== NOTIFICATIONS ====================
  
  async getNotifications(params = {}) {
    return apiClient.get('/notifications/', params);
  },

  async markNotificationAsRead(notificationId) {
    if (!notificationId) throw new Error('Notification ID is required');
    return apiClient.post(`/notifications/${notificationId}/mark-read/`);
  },

  async sendPaymentReminders(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.post(`/groups/${groupId}/send-reminders/`);
  },

  // ==================== INVITATIONS ====================
  
  async getInvitations() {
    return apiClient.get('/invitations/');
  },

  async respondToInvitation(invitationId, responseData) {
    if (!invitationId) throw new Error('Invitation ID is required');
    return apiClient.post(`/invitations/${invitationId}/respond/`, responseData);
  },

  // ==================== STATISTICS & REPORTS ====================
  
  async getGroupStatistics() {
    return apiClient.get('/statistics/');
  },

  async getGroupFinancialReport(groupId, params = {}) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/financial-report/`, params);
  },

  // ==================== GROUP VERIFICATION ====================
  
  async requestGroupVerification(groupId, verificationData) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.post(`/groups/${groupId}/request-verification/`, verificationData);
  },

  async getGroupVerificationStatus(groupId) {
    if (!groupId) throw new Error('Group ID is required');
    return apiClient.get(`/groups/${groupId}/verification-status/`);
  },

  // ==================== ADMIN ENDPOINTS ====================
  
  async getVerificationRequests(params = {}) {
    return apiClient.get('/admin/verifications/', params);
  },

  async processVerificationRequest(verificationId, actionData) {
    if (!verificationId) throw new Error('Verification ID is required');
    return apiClient.post(`/admin/verifications/${verificationId}/action/`, actionData);
  },

  async getAdminDashboard() {
    return apiClient.get('/admin/dashboard/');
  },

  async getAdminGroups(params = {}) {
    return apiClient.get('/admin/groups/', params);
  },

  async distributeReceivingContributions(cycleId, distributionData) {
    if (!cycleId) throw new Error('Cycle ID is required');
    return apiClient.post(`/cycles/${cycleId}/distribute/`, distributionData);
  },

  // ==================== UTILITY METHODS ====================
  
  formatCurrency(amount, currency = 'TZS') {
    try {
      return new Intl.NumberFormat('sw-TZ', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount || 0);
    } catch (error) {
      // Fallback for unsupported locales
      return `${currency} ${(amount || 0).toLocaleString()}`;
    }
  },

  formatDate(date, options = {}) {
    if (!date) return '';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    try {
      return new Date(date).toLocaleDateString('en-US', defaultOptions);
    } catch (error) {
      return new Date(date).toLocaleDateString();
    }
  },

  getStatusBadgeClass(status) {
    const statusClasses = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'active': 'bg-green-100 text-green-800 border-green-200',
      'verified': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-gray-100 text-gray-800 border-gray-200',
      'paid': 'bg-green-100 text-green-800 border-green-200',
      'partial': 'bg-orange-100 text-orange-800 border-orange-200',
      'overdue': 'bg-red-100 text-red-800 border-red-200',
      'approved': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'needs_info': 'bg-purple-100 text-purple-800 border-purple-200',
    };
    
    return statusClasses[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  },

  getErrorMessage(error) {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    return 'An unexpected error occurred. Please try again.';
  },

  // Debug helper
  debug() {
    const token = this.getToken();
    console.log('=== KILELENI SERVICE DEBUG ===');
    console.log('🔐 Has Token:', this.isAuthenticated());
    console.log('🌐 API Base URL:', API_CONFIG.BASE_URL);
    console.log('📱 Token Preview:', token ? `${token.substring(0, 20)}...` : 'None');
    console.log('===============================');
    
    return {
      hasToken: this.isAuthenticated(),
      apiUrl: API_CONFIG.BASE_URL,
      tokenPreview: token ? `${token.substring(0, 20)}...` : null,
    };
  },
  // ==================== TRANSACTION ACCOUNT MANAGEMENT ====================
  
  async getTransactionAccounts(params = {}) {
    return apiClient.get('/transaction-accounts/', params);
  },

  async getTransactionAccount(accountId) {
    if (!accountId) throw new Error('Account ID is required');
    return apiClient.get(`/transaction-accounts/${accountId}/`);
  },

  async createTransactionAccount(accountData) {
    if (!accountData) throw new Error('Account data is required');
    return apiClient.post('/transaction-accounts/', accountData);
  },

  async updateTransactionAccount(accountId, updateData) {
    if (!accountId) throw new Error('Account ID is required');
    return apiClient.patch(`/transaction-accounts/${accountId}/`, updateData);
  },

  async deleteTransactionAccount(accountId) {
    if (!accountId) throw new Error('Account ID is required');
    return apiClient.delete(`/transaction-accounts/${accountId}/`);
  },

  async getTransactionAccountsForDaraja(darajaName) {
    if (!darajaName) throw new Error('Daraja name is required');
    return apiClient.get(`/transaction-accounts/daraja/${darajaName}/`);
  },

  async searchTransactionAccounts(query, darajaName = null) {
    if (!query || query.length < 2) {
      throw new Error('Search query must be at least 2 characters');
    }
    const params = { q: query };
    if (darajaName) params.daraja = darajaName;
    return apiClient.get('/transaction-accounts/search/', params);
  },


  // ==================== EXPORT FUNCTIONALITY ====================
  
  async exportGroups(format = 'csv') {
    try {
      const response = await apiClient.get('/admin/export/groups/', { format });
      
      // If the backend returns the data directly
      if (typeof response === 'string') {
        return response;
      }
      
      // If we need to generate CSV from JSON data
      if (Array.isArray(response) || response.results) {
        const data = response.results || response;
        return this.convertToCSV(data, [
          'id', 'name', 'chairperson_name', 'daraja_name', 'member_count', 
          'status', 'verification_status', 'created_at'
        ]);
      }
      
      return 'No data available for export';
    } catch (error) {
      console.error('Export groups failed:', error);
      // Fallback: generate CSV from current data
      const data = await this.getAdminGroups({ page_size: 1000 });
      return this.convertToCSV(data.results || data, [
        'id', 'name', 'chairperson_name', 'daraja_name', 'member_count', 
        'status', 'verification_status', 'created_at'
      ]);
    }
  },

  async exportTransactionAccounts(format = 'csv') {
    try {
      const response = await apiClient.get('/admin/export/accounts/', { format });
      
      if (typeof response === 'string') {
        return response;
      }
      
      if (Array.isArray(response) || response.results) {
        const data = response.results || response;
        return this.convertToCSV(data, [
          'id', 'name', 'account_number', 'account_type', 'is_special', 
          'min_balance', 'is_active', 'created_at'
        ]);
      }
      
      return 'No data available for export';
    } catch (error) {
      console.error('Export accounts failed:', error);
      // Fallback: generate CSV from current data
      const data = await this.getTransactionAccounts({ page_size: 1000 });
      return this.convertToCSV(data.results || data, [
        'id', 'name', 'account_number', 'account_type', 'is_special', 
        'min_balance', 'is_active', 'created_at'
      ]);
    }
  },

  async exportVerificationRequests(format = 'csv') {
    try {
      const response = await apiClient.get('/admin/export/verifications/', { format });
      
      if (typeof response === 'string') {
        return response;
      }
      
      if (Array.isArray(response) || response.results) {
        const data = response.results || response;
        return this.convertToCSV(data, [
          'id', 'group_name', 'requested_by_name', 'group_daraja', 'group_member_count',
          'proposed_seb_amount', 'proposed_pei_amount', 'status', 'created_at'
        ]);
      }
      
      return 'No data available for export';
    } catch (error) {
      console.error('Export verifications failed:', error);
      // Fallback: generate CSV from current data
      const data = await this.getVerificationRequests({ page_size: 1000 });
      return this.convertToCSV(data.results || data, [
        'id', 'group_name', 'requested_by_name', 'group_daraja', 'group_member_count',
        'proposed_seb_amount', 'proposed_pei_amount', 'status', 'created_at'
      ]);
    }
  },

  // ==================== CSV CONVERSION UTILITY ====================
  
  convertToCSV(data, columns) {
    try {
      if (!Array.isArray(data) || data.length === 0) {
        return 'No data available';
      }

      // Create headers
      const headers = columns.map(col => {
        // Convert snake_case to Title Case
        return col.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      }).join(',');

      // Create rows
      const rows = data.map(item => {
        return columns.map(col => {
          let value = item[col];
          
          // Handle different data types
          if (value === null || value === undefined) {
            value = '';
          } else if (typeof value === 'boolean') {
            value = value ? 'Yes' : 'No';
          } else if (typeof value === 'object') {
            value = JSON.stringify(value);
          } else {
            value = String(value);
          }
          
          // Escape quotes and wrap in quotes if contains comma
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = '"' + value.replace(/"/g, '""') + '"';
          }
          
          return value;
        }).join(',');
      });

      return [headers, ...rows].join('\n');
    } catch (error) {
      console.error('CSV conversion failed:', error);
      return 'Error generating CSV data';
    }
  },

  async retryRequest(requestFn, maxRetries = 3, delay = 1000) {
    let lastError;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on authentication errors
        if (error.status === 401 || error.status === 403) {
          throw error;
        }
        
        // Don't retry on the last attempt
        if (i === maxRetries - 1) {
          break;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        console.log(`Retrying request... Attempt ${i + 2} of ${maxRetries}`);
      }
    }
    
    throw lastError;
  },
  // Delete Group function
async deleteGroup(groupId) {
  if (!groupId) throw new Error('Group ID is required');
  return apiClient.delete(`/groups/${groupId}/delete/`);
},
// Base Amount Management Functions using apiClient
  async getCurrentBaseAmount() {
    return await apiClient.get('/current-base-amount/');
  },

  async getBaseAmountSettings() {
    return await apiClient.get('/admin/base-amount-settings/');
  },

  async createBaseAmountSetting(data) {
    return await apiClient.post('/admin/base-amount-settings/create/', data);
  },

  async updateBaseAmountSetting(id, data) {
    return await apiClient.patch(`/admin/base-amount-settings/${id}/update/`, data);
  },

  async setDefaultBaseAmount(settingId) {
    return await apiClient.post(`/admin/base-amount-settings/${settingId}/set-default/`);
  },

  async setGroupCustomBaseAmount(groupId, customAmount) {
    return await apiClient.patch(`/groups/${groupId}/custom-base-amount/`, { custom_base_amount: customAmount });
  },
  // ==================== DARAJA LEVEL MANAGEMENT (ADMIN) ====================

/**
 * Get all Daraja levels with optional filters
 * @param {Object} params - Query parameters (is_active, type, search)
 */
async getDarajaLevels(params = {}) {
  return await apiClient.get('/admin/darajas/', params);
},

/**
 * Get Daraja statistics
 */
async getDarajaStats() {
  return await apiClient.get('/admin/darajas/stats/');
},

/**
 * Get single Daraja level details
 * @param {number} id - Daraja ID
 */
async getDarajaLevel(id) {
  if (!id) throw new Error('Daraja ID is required');
  return await apiClient.get(`/admin/darajas/${id}/`);
},

/**
 * Create new Daraja level
 * @param {Object} data - Daraja data
 */
async createDarajaLevel(data) {
  if (!data) throw new Error('Daraja data is required');
  if (!data.name) throw new Error('Daraja name is required');
  return await apiClient.post('/admin/darajas/create/', data);
},

/**
 * Update Daraja level
 * @param {number} id - Daraja ID
 * @param {Object} data - Update data
 */
async updateDarajaLevel(id, data) {
  if (!id) throw new Error('Daraja ID is required');
  if (!data) throw new Error('Update data is required');
  return await apiClient.patch(`/admin/darajas/${id}/update/`, data);
},

/**
 * Delete Daraja level
 * @param {number} id - Daraja ID
 */
async deleteDarajaLevel(id) {
  if (!id) throw new Error('Daraja ID is required');
  return await apiClient.delete(`/admin/darajas/${id}/delete/`);
},

/**
 * Toggle Daraja active status
 * @param {number} id - Daraja ID
 */
async toggleDarajaActive(id) {
  if (!id) throw new Error('Daraja ID is required');
  return await apiClient.post(`/admin/darajas/${id}/toggle-active/`);
},

async exportDarajaLevels(format = 'csv') {
  try {
    const response = await apiClient.get('/admin/export/darajas/', { format });
    
    if (typeof response === 'string') {
      return response;
    }
    
    if (Array.isArray(response) || response.results) {
      const data = response.results || response;
      return this.convertToCSV(data, [
        'id', 'name', 'min_amount', 'max_amount', 'total_base_amount',
        'enf_amount', 'escrow_amount', 'is_active', 'created_at'
      ]);
    }
    
    return 'No data available for export';
  } catch (error) {
    console.error('Export darajas failed:', error);
    // Fallback: generate CSV from current data
    const data = await this.getDarajaLevels({ page_size: 1000 });
    return this.convertToCSV(data.results || data, [
      'id', 'name', 'min_amount', 'max_amount', 'total_base_amount',
      'enf_amount', 'escrow_amount', 'is_active', 'created_at'
    ]);
  }
}
};

export default MbelekoService;